import axios from 'axios'
import { Download, Upload, FileText, X, Loader2 } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { inventoryService } from '@/app/services/inventory.service'
import { useAuthStore } from '@/app/store/auth/auth.store'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
interface ImportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ImportErrorResponse {
  message?: string
  errors?: Record<string, string[]>
}

function importErrorMessage(error: unknown): string {
  if (!axios.isAxiosError<ImportErrorResponse>(error)) {
    return 'Ocurrió un error inesperado al preparar la importación.'
  }

  if (error.code === 'ECONNABORTED') {
    return 'El servidor tardó demasiado en responder. Intenta nuevamente.'
  }

  if (!error.response) {
    return 'No fue posible comunicarse con el servidor.'
  }

  const validationMessage = Object.values(error.response.data?.errors ?? {})
    .flat()
    .find(Boolean)

  if (validationMessage) {
    return validationMessage
  }

  return error.response.data?.message
    ?? (error.response.status >= 500
      ? 'El servidor no pudo preparar el archivo para importación.'
      : 'No se pudo iniciar la importación.')
}

// ─── 2. Componente Principal ───────────────────────────────────────────────
export function ImportModal({ open, onOpenChange }: ImportModalProps) {
  // ─── Estados (Variables Reactivas) ───
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)

  // Store Global
  const { activeBranchId } = useAuthStore()

  // ─── Funciones de Interfaz ───
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const validExtensions = ['.xls', '.xlsx']
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()

      if (!validExtensions.includes(extension)) {
        toast.error('Formato inválido. Por favor selecciona un archivo Excel (.xls o .xlsx).')
        e.target.value = ''
        return
      }
      setSelectedFile(file)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true)

    try {
      const blob = await inventoryService.downloadProductImportTemplate()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')

      anchor.href = url
      anchor.download = 'plantilla-importacion-productos.xlsx'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)

      toast.success('Plantilla descargada correctamente')
    } catch (error) {
      toast.error('No se pudo descargar la plantilla')
      console.error(error)
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  // ─── Funciones API ───
  const handleImport = async () => {
    if (!selectedFile) return
    if (!activeBranchId) {
      toast.error('No hay una sucursal activa seleccionada.')
      return
    }

    setIsUploading(true)
    try {
      await inventoryService.importProducts(selectedFile, activeBranchId)
      toast.success('Procesando en segundo plano', {
        description: 'El archivo fue recibido y se está preparando para importar.'
      })
      onOpenChange(false)
      setSelectedFile(null)
    } catch (error) {
      toast.error('Error al iniciar la importación', {
        description: importErrorMessage(error)
      })
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (isUploading) return
      onOpenChange(val)
      if (!val) setSelectedFile(null)
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Importar Productos desde Excel</DialogTitle>
          <DialogDescription className="sr-only">
            Cargar productos masivamente desde un archivo Excel
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="mb-2 text-sm font-medium text-green-900">
              Instrucciones:
            </p>
            <ol className="list-inside list-decimal space-y-1 text-sm text-green-800">
              <li>Descarga la plantilla de Excel</li>
              <li>Completa los datos de los productos</li>
              <li>Sube el archivo completo</li>
            </ol>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleDownloadTemplate}
            disabled={isUploading || isDownloadingTemplate}
          >
            {isDownloadingTemplate ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {isDownloadingTemplate ? 'Descargando plantilla...' : 'Descargar Plantilla Excel'}
          </Button>

          <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              id="excel-upload"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            {selectedFile ? (
              <div className="flex flex-col items-center justify-center space-y-2">
                <FileText className="h-12 w-12 text-blue-500" />
                <div className="flex flex-col items-center w-full px-4">
                  <p className="text-sm font-medium text-slate-700 break-all text-center" title={selectedFile.name}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('excel-upload')?.click()}
                    disabled={isUploading}
                    className="text-slate-600 hover:text-slate-800"
                  >
                    Reemplazar archivo
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={isUploading}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <label htmlFor="excel-upload" className="cursor-pointer block w-full">
                <Upload className="mx-auto mb-2 h-12 w-12 text-slate-400" />
                <p className="text-sm text-slate-600">
                  Haz clic para seleccionar archivo
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Solo archivos .xlsx o .xls
                </p>
              </label>
            )}
          </div>

          <Button
            className="w-full bg-primary hover:bg-primary/90"
            onClick={handleImport}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Iniciando importación...
              </>
            ) : (
              'Importar Productos'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
